import { classificarLimite, diasEntre } from "../../../hooks/nova-obra.condicoes";
import type { ObraLimiteClasse, PeriodoObraEtapa } from "@/modules/obras";

/**
 * Categoria de cor da barra (Figma `duracao/barra-periodo` 10413:96245 e regras 12223:137954): a cor
 * sai da classificação (`etapa`) do período no catálogo, nunca do nome. Execução (a "Obra" em si) é a
 * linha "Período de obra", calculada a partir dos períodos de Obra, e Total é a primeira linha.
 */
export type CategoriaGantt = "navegacao" | "permanencia" | "comissionamento";

const CATEGORIA_POR_ETAPA: Record<PeriodoObraEtapa, CategoriaGantt> = {
  NAVEGACAO_ENTRADA: "navegacao",
  NAVEGACAO_SAIDA: "navegacao",
  OBRA: "permanencia",
  TESTES: "comissionamento",
  ACEITACAO: "comissionamento",
};

/** Período sem classificação no cadastro: segue como Permanência (a barra de maior presença). */
export const categoriaDoPeriodo = (etapa: PeriodoObraEtapa | null): CategoriaGantt =>
  etapa ? CATEGORIA_POR_ETAPA[etapa] : "permanencia";

/** Recuo do rótulo do mês em relação à divisória (Figma 10706:406671: 8 px). */
export const MES_RECUO_ROTULO_PX = 8;
/** "Mmm AAAA" tem 50 a 56 px a 14/500 (Figma 10706:406671..406674): mais o recuo de 8 e um respiro de 8. */
export const MES_MIN_ROTULO_LONGO_PX = 72;
/** "Mmm" tem cerca de 26 px: mais o recuo de 8 e um respiro de 6. */
export const MES_MIN_ROTULO_CURTO_PX = 40;

/** Folga do eixo antes da primeira data e depois da última (Figma 10706:406743: 4 dias de cada lado). */
export const FOLGA_EIXO_DIAS = 4;
/** Folga para o limite de classe que cai fora do eixo do Total da parada. */
export const FOLGA_LIMITE_DIAS = 2;

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export interface GanttPeriodoEntrada {
  readonly uid: string;
  readonly rotulo: string;
  readonly etapa: PeriodoObraEtapa | null;
  readonly inicio: string | null;
  readonly fim: string | null;
  readonly duracao: number | null;
  readonly meta: number | null;
}

export interface GanttBarra {
  readonly uid: string;
  readonly rotulo: string;
  readonly categoria: CategoriaGantt;
  readonly inicio: string;
  readonly fim: string;
  readonly dias: number;
  readonly meta: number | null;
  /** Posição e largura no eixo, em % da trilha. */
  readonly esquerda: number;
  readonly largura: number;
}

export interface GanttSegmento {
  readonly inicio: string;
  readonly fim: string;
  readonly dias: number;
  readonly esquerda: number;
  readonly largura: number;
}

export interface GanttMes {
  /** `Nov 2026`. */
  readonly rotulo: string;
  /** `Nov`: o que sobra do rótulo quando o mês é estreito demais para o ano. */
  readonly rotuloCurto: string;
  readonly esquerda: number;
  /** Largura do mês dentro do eixo, em % da trilha (o primeiro e o último costumam ser parciais). */
  readonly largura: number;
  /** O primeiro mês começa na borda da trilha e não leva divisória. */
  readonly divisoria: boolean;
}

export interface GanttLimite {
  readonly nome: string;
  readonly data: string;
  readonly esquerda: number;
  /** Índice da última linha (0 = Total da parada) que a linha tracejada atravessa. */
  readonly ultimaLinha: number;
}

export interface GanttModelo {
  /** Primeiro e último dia do eixo (com as folgas): a escala da base do Gantt sai deles. */
  readonly eixo: { readonly inicio: string; readonly fim: string };
  readonly meses: readonly GanttMes[];
  readonly total: GanttSegmento;
  readonly obra: GanttSegmento | null;
  readonly periodos: readonly GanttBarra[];
  readonly limites: readonly GanttLimite[];
}

const somarDias = (iso: string, dias: number): string => {
  const data = new Date(`${iso}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
};

const menor = (a: string, b: string) => (a < b ? a : b);
const maior = (a: string, b: string) => (a > b ? a : b);

interface EntradaGantt {
  readonly periodos: readonly GanttPeriodoEntrada[];
  /** Menor e maior data do cronograma (Total da parada). */
  readonly total: { readonly inicio: string; readonly fim: string } | null;
  /** Intervalo dos períodos classificados como Obra (linha "Período de obra"). */
  readonly obra: { readonly inicio: string; readonly fim: string } | null;
  readonly limites: readonly ObraLimiteClasse[];
  readonly hoje: string;
}

/**
 * Limites que entram no Gantt: os que vencem antes do início ou durante a obra (os mesmos que geram
 * as mensagens da HU 2549). Vencidos (antes de hoje) e posteriores ao fim ficam de fora.
 */
export function limitesDoGantt(
  limites: readonly ObraLimiteClasse[],
  periodo: { inicio: string; fim: string },
  hoje: string,
): ObraLimiteClasse[] {
  return limites
    .filter((limite) => {
      const relacao = classificarLimite(limite.vencimento, periodo, hoje);
      return relacao === "ANTES_DO_INICIO" || relacao === "DURANTE_A_OBRA";
    })
    .sort(
      (a, b) => a.vencimento.localeCompare(b.vencimento) || a.nome.localeCompare(b.nome, "pt-BR"),
    );
}

/** Monta o Gantt da Revisão (Figma `gantt-parada` 10706:406743); `null` sem cronograma calculável. */
export function montarGantt({
  periodos,
  total,
  obra,
  limites,
  hoje,
}: EntradaGantt): GanttModelo | null {
  if (!total) return null;

  const relevantes = limitesDoGantt(limites, total, hoje);
  // O eixo tem 4 dias de folga em volta do Total da parada; um limite fora dele estende o eixo com 2 dias.
  const datasLimite = relevantes.map((limite) => limite.vencimento);
  const eixoInicio = menor(
    somarDias(total.inicio, -FOLGA_EIXO_DIAS),
    somarDias(datasLimite.reduce(menor, total.inicio), -FOLGA_LIMITE_DIAS),
  );
  const eixoFim = maior(
    somarDias(total.fim, FOLGA_EIXO_DIAS),
    somarDias(datasLimite.reduce(maior, total.fim), FOLGA_LIMITE_DIAS),
  );
  const eixoDias = diasEntre(eixoInicio, eixoFim) + 1;

  const posicao = (data: string) => (diasEntre(eixoInicio, data) / eixoDias) * 100;
  const segmento = (inicio: string, fim: string): GanttSegmento => {
    const dias = diasEntre(inicio, fim) + 1;
    return { inicio, fim, dias, esquerda: posicao(inicio), largura: (dias / eixoDias) * 100 };
  };

  const barras: GanttBarra[] = periodos.flatMap((periodo) => {
    if (!periodo.inicio || !periodo.fim) return [];
    const base = segmento(periodo.inicio, periodo.fim);
    return [
      {
        uid: periodo.uid,
        rotulo: periodo.rotulo,
        categoria: categoriaDoPeriodo(periodo.etapa),
        inicio: base.inicio,
        fim: base.fim,
        dias: periodo.duracao ?? base.dias,
        meta: periodo.meta,
        esquerda: base.esquerda,
        largura: base.largura,
      },
    ];
  });

  const meses: GanttMes[] = [];
  const cursor = new Date(`${eixoInicio}T00:00:00Z`);
  while (cursor.toISOString().slice(0, 10) <= eixoFim) {
    const iso = cursor.toISOString().slice(0, 10);
    const comecoDoMes = iso === eixoInicio ? eixoInicio : `${iso.slice(0, 8)}01`;
    meses.push({
      rotulo: `${MESES[cursor.getUTCMonth()]} ${cursor.getUTCFullYear()}`,
      rotuloCurto: MESES[cursor.getUTCMonth()] ?? "",
      esquerda: posicao(comecoDoMes),
      largura: 0,
      divisoria: comecoDoMes !== eixoInicio,
    });
    cursor.setUTCDate(1);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  // Cada mês vai até o começo do seguinte (o último, até o fim do eixo).
  meses.forEach((mes, indice) => {
    const fimDoMes = meses[indice + 1]?.esquerda ?? 100;
    meses[indice] = { ...mes, largura: fimDoMes - mes.esquerda };
  });

  // As linhas de limite ficam no fim da tabela: Total, Período de obra (se houver), uma por período.
  const linhasAntesDosLimites = 1 + (obra ? 1 : 0) + barras.length;
  const noGantt = relevantes.map((limite, indice) => ({
    nome: limite.nome,
    data: limite.vencimento,
    esquerda: posicao(limite.vencimento),
    indice,
  }));
  const ultimaPorData = new Map<string, number>();
  noGantt.forEach(({ data, indice }) => ultimaPorData.set(data, linhasAntesDosLimites + indice));

  return {
    eixo: { inicio: eixoInicio, fim: eixoFim },
    meses,
    total: segmento(total.inicio, total.fim),
    obra: obra ? segmento(obra.inicio, obra.fim) : null,
    periodos: barras,
    limites: noGantt.map(({ nome, data, esquerda }) => ({
      nome,
      data,
      esquerda,
      ultimaLinha: ultimaPorData.get(data) ?? linhasAntesDosLimites,
    })),
  };
}

/**
 * Rótulo de cada mês do cabeçalho, de acordo com a largura que o mês tem na trilha, para que um não
 * encoste no outro nem passe da borda (o Figma só desenha 3 meses largos): com espaço, "Nov 2026"; sem,
 * "Nov"; ainda mais estreito (eixo de 1 ano ou mais), um rótulo a cada N meses, com N a menor
 * quantidade que dá espaço ao "Nov". `null` = sem rótulo (o mês é uma lasca do começo ou do fim do eixo).
 */
export function rotulosDosMeses(
  meses: readonly GanttMes[],
  larguraTrilhaPx: number,
): (string | null)[] {
  const larguras = meses.map((mes) => (mes.largura / 100) * larguraTrilhaPx);
  const maior = Math.max(0, ...larguras);

  if (maior >= MES_MIN_ROTULO_LONGO_PX) {
    return meses.map((mes, indice) => {
      const largura = larguras[indice] ?? 0;
      if (largura >= MES_MIN_ROTULO_LONGO_PX) return mes.rotulo;
      return largura >= MES_MIN_ROTULO_CURTO_PX ? mes.rotuloCurto : null;
    });
  }
  if (maior >= MES_MIN_ROTULO_CURTO_PX) {
    return meses.map((mes, indice) =>
      (larguras[indice] ?? 0) >= MES_MIN_ROTULO_CURTO_PX ? mes.rotuloCurto : null,
    );
  }

  const passo = Math.ceil(MES_MIN_ROTULO_CURTO_PX / Math.max(maior, 1));
  return meses.map((mes, indice) => {
    if (indice % passo !== 0) return null;
    const espaco = larguras
      .slice(indice, indice + passo)
      .reduce((soma, largura) => soma + largura, 0);
    return espaco >= MES_MIN_ROTULO_CURTO_PX ? mes.rotuloCurto : null;
  });
}
